import { create } from 'zustand'
import type { Point } from './geometry'

// Transient canvas state — pan/zoom, selection, drag overrides, and pending items.
// The authoritative item list lives in tRPC's query cache; this store only holds
// view state and optimistic overlays applied while the user is interacting.

type DragOverride = { position: Point }
type ResizeOverride = { size: { w: number; h: number } }

// An item that's been queued for add but hasn't been persisted yet. Rendered as an
// optimistic placeholder so the canvas reacts immediately to drops / pastes / picks.
// Cleared once the real item lands in the tRPC cache.
export type PendingItem = {
  id: string
  position: Point
  size: { w: number; h: number }
  kind: 'image' | 'video' | 'pdf' | 'font' | 'file' | 'text' | 'url' | 'color'
  // Local preview URL (Object URL) for images/videos so the user sees content instantly,
  // not just a spinner. Caller is responsible for revoking on cleanup.
  previewUrl?: string
  label?: string
}

type CanvasState = {
  scale: number
  offset: Point
  viewport: { width: number; height: number }
  selectedIds: Set<string>
  dragOverrides: Record<string, DragOverride>
  resizeOverrides: Record<string, ResizeOverride>
  pendingItems: PendingItem[]
  // Counts pastes of canvas-internal items since the last copy. Used to stagger
  // successive pastes so they don't stack. Reset whenever a fresh copy happens.
  pasteCount: number
  // Item ids currently being enriched in the background (URL scrape, video
  // analysis, etc). Cards show a small pulsing indicator while their id is here;
  // Analyze gates on this set being empty.
  enrichingIds: Set<string>

  setTransform: (patch: Partial<Pick<CanvasState, 'scale' | 'offset'>>) => void
  setViewport: (size: { width: number; height: number }) => void
  // Id of a color card that was just placed via the toolbar; the matching ColorCard opens
  // its picker on mount (then clears this), so the user can pick a color without an extra
  // click. null when nothing is pending.
  justPlacedColorId: string | null
  setJustPlacedColorId: (id: string | null) => void
  setSelected: (next: Set<string> | ((prev: Set<string>) => Set<string>)) => void
  // Pointer-driven selection for canvas items. Shift toggles the item in/out of the
  // selection; a plain click selects just that item, unless it's already part of a
  // multi-selection (then the selection is preserved so a drag moves the whole group).
  selectFromPointer: (id: string, shiftKey: boolean) => void
  beginDrag: (id: string) => void
  updateDrag: (id: string, position: Point) => void
  endDrag: (id: string) => void
  clearAllDrags: () => void
  beginResize: (id: string, size: { w: number; h: number }) => void
  updateResize: (id: string, size: { w: number; h: number }) => void
  endResize: (id: string) => void
  addPending: (item: PendingItem) => void
  updatePending: (id: string, patch: Partial<PendingItem>) => void
  removePending: (id: string) => void
  resetPasteCount: () => void
  bumpPasteCount: () => void
  markEnriching: (id: string) => void
  clearEnriching: (id: string) => void
}

export const useCanvasStore = create<CanvasState>((set) => ({
  scale: 1,
  offset: { x: 0, y: 0 },
  viewport: { width: 0, height: 0 },
  selectedIds: new Set<string>(),
  dragOverrides: {},
  resizeOverrides: {},
  pendingItems: [],
  pasteCount: 0,
  enrichingIds: new Set<string>(),
  justPlacedColorId: null,

  setJustPlacedColorId: (id) => set({ justPlacedColorId: id }),
  setTransform: (patch) => set((s) => ({ ...s, ...patch })),
  setViewport: (viewport) => set({ viewport }),
  setSelected: (next) =>
    set((s) => ({
      selectedIds: typeof next === 'function' ? next(s.selectedIds) : next,
    })),
  selectFromPointer: (id, shiftKey) =>
    set((s) => {
      if (shiftKey) {
        const next = new Set(s.selectedIds)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return { selectedIds: next }
      }
      // Preserve an existing multi-selection that includes this item so a drag moves the
      // whole group; otherwise select just this item.
      if (s.selectedIds.has(id) && s.selectedIds.size > 1) return s
      return { selectedIds: new Set([id]) }
    }),
  beginDrag: (id) =>
    set((s) => ({
      dragOverrides: { ...s.dragOverrides, [id]: { position: { x: 0, y: 0 } } },
    })),
  updateDrag: (id, position) =>
    set((s) => ({ dragOverrides: { ...s.dragOverrides, [id]: { position } } })),
  endDrag: (id) =>
    set((s) => {
      const next = { ...s.dragOverrides }
      delete next[id]
      return { dragOverrides: next }
    }),
  clearAllDrags: () => set({ dragOverrides: {} }),
  beginResize: (id, size) =>
    set((s) => ({ resizeOverrides: { ...s.resizeOverrides, [id]: { size } } })),
  updateResize: (id, size) =>
    set((s) => ({ resizeOverrides: { ...s.resizeOverrides, [id]: { size } } })),
  endResize: (id) =>
    set((s) => {
      const next = { ...s.resizeOverrides }
      delete next[id]
      return { resizeOverrides: next }
    }),
  addPending: (item) => set((s) => ({ pendingItems: [...s.pendingItems, item] })),
  updatePending: (id, patch) =>
    set((s) => ({
      pendingItems: s.pendingItems.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    })),
  removePending: (id) => set((s) => ({ pendingItems: s.pendingItems.filter((p) => p.id !== id) })),
  resetPasteCount: () => set({ pasteCount: 0 }),
  bumpPasteCount: () => set((s) => ({ pasteCount: s.pasteCount + 1 })),
  markEnriching: (id) =>
    set((s) => {
      if (s.enrichingIds.has(id)) return s
      const next = new Set(s.enrichingIds)
      next.add(id)
      return { enrichingIds: next }
    }),
  clearEnriching: (id) =>
    set((s) => {
      if (!s.enrichingIds.has(id)) return s
      const next = new Set(s.enrichingIds)
      next.delete(id)
      return { enrichingIds: next }
    }),
}))
